import { useMemo } from "react";
import { FetcherWithComponents, useFetcher } from "react-router-dom";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { RiArrowDropDownLine } from "@remixicon/react";

import { cx } from "utils";
import { getCouponDiscountText } from "utils/planUtils";
import CouponCodeInput from "v2/components/billing/CouponCodeInput";
import { useActiveCoupons } from "routes/data-routes/billing-data/coupons/active-coupons";
import { CheckCouponsFetcherDataType } from "routes/data-routes/billing-data/coupons/check-coupons/route.meta";
import { Coupon } from "types/Coupons";
import { BillingInputName } from "v2/dataroutes/BillingData";


function getApplicableCoupons(coupons: Coupon[] | null | undefined, targetPlanSlug: string | undefined): Coupon[] {
  if (!coupons?.length) return [];
  if (!targetPlanSlug) return coupons;

  return coupons.filter(
    coupon => !coupon.applicable_plan_ids || coupon.applicable_plan_ids.includes(targetPlanSlug)
  );
}

interface CouponContentProps {
  targetPlanSlug?: string;
  onUserInput?: () => void;
  needExtraForm?: boolean;
  couponsFetcher?: FetcherWithComponents<CheckCouponsFetcherDataType>;
  unmountOnClose?: boolean;
}

export default function CouponContent({ targetPlanSlug, onUserInput, needExtraForm, couponsFetcher, unmountOnClose = true }: CouponContentProps) {
  const couponFetcher = useFetcher<CheckCouponsFetcherDataType>();
  const { activeCoupons } = useActiveCoupons();

  const applicableCoupons = useMemo(
    () => getApplicableCoupons(activeCoupons, targetPlanSlug),
    [activeCoupons, targetPlanSlug]
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Active applicable coupons */}
      {applicableCoupons.length > 0 && (
        <div className="flex flex-col gap-2">
          {applicableCoupons.map((coupon) => (
            <div key={coupon.coupon_code} className="flex items-center gap-2 p-3 bg-neutral-50 border border-neutral-50 rounded">
              <span className="flex flex-shrink-0 px-1.5 justify-center items-center gap-1 self-stretch bg-neutral-600 text-white text-base font-medium leading-6 rounded-sm whitespace-nowrap">
                {coupon.coupon_code}
              </span>
              <span className="flex-1 text-base text-gray-900 truncate">
                {coupon.coupon_name || getCouponDiscountText(coupon) || "Discount"}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Add coupon input — key forces remount when coupons load so defaultOpen applies correctly */}
      <Disclosure key={applicableCoupons.length > 0 ? "has-coupons" : "no-coupons"} defaultOpen={applicableCoupons.length === 0}>
        { ({ open }) => (
          <>
            <DisclosureButton>
              <div className="flex flex-row text-brandPrimary dark:text-primary-600">
                {applicableCoupons.length > 0 ? "Add another coupon?" : "Have a coupon?"}
                <RiArrowDropDownLine className={ cx(open && "-rotate-180", "transition-transform duration-300" ) }/>
              </div>
            </DisclosureButton>

            {/* make sure the plan slug is in a hidden input even if the disclosure is closed */}
            { !open && (
              <input type="hidden" name={ BillingInputName.targetPlanSlug } value={ targetPlanSlug }/>
            ) }

            <DisclosurePanel
              transition
              className="origin-top transition duration-200 ease-out data-[closed]:scale-y-95 data-[closed]:opacity-0"
              unmount={ unmountOnClose }
            >
              { (needExtraForm === false) && (
                <CouponCodeInput fetcher={ couponsFetcher ?? couponFetcher } onUserInput={ onUserInput } targetPlanSlug={ targetPlanSlug } />
              ) }
              { (needExtraForm !== false) && (
                <form onSubmit={ (e) => e.preventDefault() } className="flex flex-row items-end w-full">
                  <div className="grid grid-rows-[min-content_min-content] grid-cols-[auto_min-content] grid-flow-col w-full gap-x-3">
                    <CouponCodeInput fetcher={ couponsFetcher ?? couponFetcher } onUserInput={ onUserInput } targetPlanSlug={ targetPlanSlug } />
                  </div>
                </form>
              ) }
            </DisclosurePanel>
          </>
        )}
      </Disclosure>
    </div>
  );
}
